import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Settings as SettingsIcon, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type Platform = "professional" | "patient";
type Channel = "platform" | "email" | "whatsapp";

type Extension = { id: string; title: string; slug: string };

type ActionRow = {
  id: string;
  platform: Platform;
  extension_id: string | null;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
};

type ChannelRow = {
  id: string;
  action_id: string;
  channel: Channel;
  enabled: boolean;
};

type TemplateRow = {
  id: string;
  action_id: string;
  channel: Channel;
  subject: string | null;
  body: string | null;
};

const PLATFORM_LABEL: Record<Platform, string> = {
  professional: "App Profissionais",
  patient: "App Pacientes",
};

const CHANNEL_LABEL: Record<Channel, string> = {
  platform: "Plataforma",
  email: "E-mail",
  whatsapp: "WhatsApp",
};

const ALL_CHANNELS: Channel[] = ["platform", "email", "whatsapp"];

const GENERAL_VALUE = "__general__";

export default function ConfiguracoesNotificacoes() {
  const [platform, setPlatform] = useState<Platform>("professional");
  const [extensionId, setExtensionId] = useState<string>(GENERAL_VALUE);

  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [actions, setActions] = useState<ActionRow[]>([]);
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<ActionRow> | null>(null);

  const [tplOpen, setTplOpen] = useState(false);
  const [tplAction, setTplAction] = useState<ActionRow | null>(null);
  const [templates, setTemplates] = useState<Record<Channel, TemplateRow | null>>({
    platform: null,
    email: null,
    whatsapp: null,
  });

  useEffect(() => {
    loadExtensions();
  }, []);

  useEffect(() => {
    loadActions();
  }, [platform, extensionId]);

  async function loadExtensions() {
    const { data } = await supabase
      .from("extensions")
      .select("id, title, slug")
      .order("title");
    setExtensions((data as Extension[]) ?? []);
  }

  async function loadActions() {
    setLoading(true);
    let q = supabase
      .from("notification_actions")
      .select("*")
      .eq("platform", platform)
      .order("name");
    q = extensionId === GENERAL_VALUE ? q.is("extension_id", null) : q.eq("extension_id", extensionId);
    const { data: actionData, error } = await q;
    if (error) {
      toast.error("Erro ao carregar ações: " + error.message);
      setLoading(false);
      return;
    }
    const acts = (actionData as ActionRow[]) ?? [];
    setActions(acts);

    if (acts.length > 0) {
      const ids = acts.map((a) => a.id);
      const { data: chData } = await supabase
        .from("notification_action_channels")
        .select("*")
        .in("action_id", ids);
      setChannels((chData as ChannelRow[]) ?? []);
    } else {
      setChannels([]);
    }
    setLoading(false);
  }

  const channelsByAction = useMemo(() => {
    const map: Record<string, Record<Channel, ChannelRow | undefined>> = {};
    for (const c of channels) {
      if (!map[c.action_id]) map[c.action_id] = { platform: undefined, email: undefined, whatsapp: undefined };
      map[c.action_id][c.channel] = c;
    }
    return map;
  }, [channels]);

  function openCreate() {
    setEditing({
      platform,
      extension_id: extensionId === GENERAL_VALUE ? null : extensionId,
      code: "",
      name: "",
      description: "",
      is_active: true,
    });
    setEditOpen(true);
  }

  function openEdit(a: ActionRow) {
    setEditing({ ...a });
    setEditOpen(true);
  }

  async function saveAction() {
    if (!editing?.name || !editing?.code) {
      toast.error("Nome e código são obrigatórios");
      return;
    }
    const payload = {
      platform: editing.platform!,
      extension_id: editing.extension_id ?? null,
      code: editing.code!,
      name: editing.name!,
      description: editing.description ?? null,
      is_active: editing.is_active ?? true,
    };
    if (editing.id) {
      const { error } = await supabase.from("notification_actions").update(payload).eq("id", editing.id);
      if (error) return toast.error(error.message);
      toast.success("Ação atualizada");
    } else {
      const { data, error } = await supabase
        .from("notification_actions")
        .insert(payload)
        .select()
        .single();
      if (error) return toast.error(error.message);
      // Seed channels + templates
      const newId = (data as ActionRow).id;
      await supabase.from("notification_action_channels").insert(
        ALL_CHANNELS.map((ch) => ({ action_id: newId, channel: ch, enabled: ch === "platform" })),
      );
      await supabase.from("notification_templates").insert(
        ALL_CHANNELS.map((ch) => ({ action_id: newId, channel: ch, subject: ch === "email" ? "" : null, body: "" })),
      );
      toast.success("Ação criada");
    }
    setEditOpen(false);
    setEditing(null);
    loadActions();
  }

  async function deleteAction(a: ActionRow) {
    if (!confirm(`Excluir a ação "${a.name}"?`)) return;
    const { error } = await supabase.from("notification_actions").delete().eq("id", a.id);
    if (error) return toast.error(error.message);
    toast.success("Ação excluída");
    loadActions();
  }

  async function toggleChannel(action: ActionRow, channel: Channel, enabled: boolean) {
    const existing = channelsByAction[action.id]?.[channel];
    if (existing) {
      const { error } = await supabase
        .from("notification_action_channels")
        .update({ enabled })
        .eq("id", existing.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase
        .from("notification_action_channels")
        .insert({ action_id: action.id, channel, enabled });
      if (error) return toast.error(error.message);
    }
    loadActions();
  }

  async function openTemplates(action: ActionRow) {
    setTplAction(action);
    setTplOpen(true);
    const { data } = await supabase
      .from("notification_templates")
      .select("*")
      .eq("action_id", action.id);
    const next: Record<Channel, TemplateRow | null> = { platform: null, email: null, whatsapp: null };
    for (const t of (data as TemplateRow[]) ?? []) next[t.channel] = t;
    // ensure rows exist for all channels in UI
    for (const ch of ALL_CHANNELS) {
      if (!next[ch]) {
        next[ch] = {
          id: "",
          action_id: action.id,
          channel: ch,
          subject: ch === "email" ? "" : null,
          body: "",
        };
      }
    }
    setTemplates(next);
  }

  async function saveTemplate(ch: Channel) {
    const t = templates[ch];
    if (!t || !tplAction) return;
    if (t.id) {
      const { error } = await supabase
        .from("notification_templates")
        .update({ subject: t.subject, body: t.body })
        .eq("id", t.id);
      if (error) return toast.error(error.message);
    } else {
      const { data, error } = await supabase
        .from("notification_templates")
        .insert({ action_id: tplAction.id, channel: ch, subject: t.subject, body: t.body })
        .select()
        .single();
      if (error) return toast.error(error.message);
      setTemplates({ ...templates, [ch]: data as TemplateRow });
    }
    toast.success(`Mensagem "${CHANNEL_LABEL[ch]}" salva`);
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Bell className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold">Notificações</h1>
          <p className="text-sm text-muted-foreground">
            Configure as ações que disparam notificações em cada plataforma e aplicativo.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Plataforma</Label>
          <Select value={platform} onValueChange={(v) => setPlatform(v as Platform)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="professional">App Profissionais</SelectItem>
              <SelectItem value="patient">App Pacientes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Aplicativo</Label>
          <Select value={extensionId} onValueChange={setExtensionId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={GENERAL_VALUE}>Geral / Plataforma</SelectItem>
              {extensions.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Actions table */}
      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <p className="text-sm font-medium">
              Ações de {PLATFORM_LABEL[platform]}{extensionId !== GENERAL_VALUE && " — " + (extensions.find(e => e.id === extensionId)?.title ?? "")}
            </p>
            <p className="text-xs text-muted-foreground">
              {actions.length} {actions.length === 1 ? "ação cadastrada" : "ações cadastradas"}
            </p>
          </div>
          <Button onClick={openCreate} size="sm">
            <Plus className="h-4 w-4 mr-1" /> Nova ação
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ação</TableHead>
              <TableHead className="text-center">Plataforma</TableHead>
              <TableHead className="text-center">E-mail</TableHead>
              <TableHead className="text-center">WhatsApp</TableHead>
              <TableHead className="w-32 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">Carregando…</TableCell></TableRow>
            )}
            {!loading && actions.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">Nenhuma ação cadastrada para este filtro.</TableCell></TableRow>
            )}
            {actions.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{a.name}</span>
                      {!a.is_active && <Badge variant="secondary">inativa</Badge>}
                    </div>
                    {a.description && <span className="text-xs text-muted-foreground">{a.description}</span>}
                    <code className="text-[10px] text-muted-foreground/80 mt-0.5">{a.code}</code>
                  </div>
                </TableCell>
                {ALL_CHANNELS.map((ch) => (
                  <TableCell key={ch} className="text-center">
                    <Switch
                      checked={!!channelsByAction[a.id]?.[ch]?.enabled}
                      onCheckedChange={(v) => toggleChannel(a, ch, v)}
                    />
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openTemplates(a)} title="Configurar mensagens">
                      <SettingsIcon className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(a)} title="Editar">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteAction(a)} title="Excluir">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit/create action dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editar ação" : "Nova ação"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input value={editing.name ?? ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label>Código</Label>
                <Input
                  value={editing.code ?? ""}
                  onChange={(e) => setEditing({ ...editing, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                  placeholder="ex: paciente_cadastrado"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Identificador interno (apenas letras minúsculas, números e _).</p>
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea
                  value={editing.description ?? ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Ativa</Label>
                <Switch
                  checked={editing.is_active ?? true}
                  onCheckedChange={(v) => setEditing({ ...editing, is_active: v })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button onClick={saveAction}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Templates dialog */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Mensagens de "{tplAction?.name}"</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="platform">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="platform">Plataforma</TabsTrigger>
              <TabsTrigger value="email">E-mail</TabsTrigger>
              <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
            </TabsList>

            <TabsContent value="platform" className="space-y-4 pt-4">
              <div>
                <Label>Título</Label>
                <Input
                  value={templates.platform?.subject ?? ""}
                  onChange={(e) => setTemplates({ ...templates, platform: { ...(templates.platform as TemplateRow), subject: e.target.value } })}
                  placeholder="Ex: Novo paciente cadastrado"
                />
              </div>
              <div>
                <Label>Mensagem</Label>
                <Textarea
                  rows={5}
                  value={templates.platform?.body ?? ""}
                  onChange={(e) => setTemplates({ ...templates, platform: { ...(templates.platform as TemplateRow), body: e.target.value } })}
                  placeholder="Ex: O paciente {{patient_name}} foi cadastrado."
                />
              </div>
              <Button onClick={() => saveTemplate("platform")}>Salvar</Button>
            </TabsContent>

            <TabsContent value="email" className="space-y-4 pt-4">
              <div>
                <Label>Assunto</Label>
                <Input
                  value={templates.email?.subject ?? ""}
                  onChange={(e) => setTemplates({ ...templates, email: { ...(templates.email as TemplateRow), subject: e.target.value } })}
                />
              </div>
              <div>
                <Label>Corpo do e-mail</Label>
                <Textarea
                  rows={10}
                  value={templates.email?.body ?? ""}
                  onChange={(e) => setTemplates({ ...templates, email: { ...(templates.email as TemplateRow), body: e.target.value } })}
                  placeholder="Aceita HTML básico. Use {{variavel}} para substituições."
                />
              </div>
              <Button onClick={() => saveTemplate("email")}>Salvar</Button>
            </TabsContent>

            <TabsContent value="whatsapp" className="space-y-4 pt-4">
              <div>
                <Label>Mensagem</Label>
                <Textarea
                  rows={6}
                  value={templates.whatsapp?.body ?? ""}
                  onChange={(e) => setTemplates({ ...templates, whatsapp: { ...(templates.whatsapp as TemplateRow), body: e.target.value } })}
                  placeholder="Texto simples. Limite recomendado: 1024 caracteres."
                />
                <p className="text-[11px] text-muted-foreground mt-1">{(templates.whatsapp?.body ?? "").length} caracteres</p>
              </div>
              <Button onClick={() => saveTemplate("whatsapp")}>Salvar</Button>
            </TabsContent>
          </Tabs>

          <div className="text-[11px] text-muted-foreground border-t pt-3">
            Variáveis disponíveis: <code>{"{{user_name}}"}</code>, <code>{"{{patient_name}}"}</code>, <code>{"{{link}}"}</code>, <code>{"{{app_name}}"}</code>.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
